// WHAT KIND of run this is, and the order the scheduler prefers them in.
// See `../task-envelope.ts` for the file docblock.

import { z } from 'zod';

/**
 * Scheduling PRIORITY class (arc 101 slice 06a, doc 06 "Fleet scheduler"). This is
 * the ARBITER dimension — how the assigner drains work when boxes are scarce — NOT
 * which model runs the task (that is `JobClass` in `job-class-routing.ts`, the
 * orthogonal LLM-routing dimension; a task carries BOTH a scheduling class and a job
 * class). `claimNextReady` (`@developerz/db`) orders ready tasks by this class then
 * priority, and the idle-box arbiter (`pickLaneForIdleBox`, `apps/worker`) tie-breaks
 * by it:
 *
 *   - `interactive` — work someone is WAITING on. Never starves behind batch work: it
 *     event-kicks the assigner (no 3s tick wait) and wins the last idle box. Two
 *     producers: the live scout/plan sessions (`plan_start` / `project setup`), and
 *     the `pr_review` lane (`@developerz/review` `REVIEW_TASK_CLASS`) — a review is
 *     not batch work because `guardedMerge` refuses with `review_in_flight` while one
 *     is unterminated, so a review left on the `coding` rank stalls every merge behind
 *     the account's coding backlog. Note this rank grants ONLY scheduling: the mailbox
 *     is keyed on ORIGIN (`interactiveRoleFor`, `apps/runner`), the credential
 *     environment is `dev` for `interactive` and `coding` alike
 *     (`secretEnvironmentForTaskClass`), and no work budget or seat ceiling reads the
 *     class at all.
 *   - `coding` — the default: ordinary autonomous coding tasks.
 *   - `ci` — idle-box CI backfill. Yields the last idle box to coding / interactive.
 *   - `gardener` — box-maintenance janitors (KB consolidation, cache warming). Only
 *     ever fills a box no higher class wants.
 *
 * Additive + OPTIONAL on the wire (backward compatible — pre-06a envelopes omit it,
 * and a producer a later slice hasn't taught to stamp one still dispatches). Read
 * through {@link taskClassOf}, which supplies the `coding` default in one place. The
 * wire field key is the bare word `class` — a JS reserved word, but legal as an
 * object key and in member access.
 */
export const TaskClass = {
  Interactive: 'interactive',
  Coding: 'coding',
  Ci: 'ci',
  Gardener: 'gardener',
} as const;
export const TaskClassSchema = z.enum(TaskClass);
export type TaskClass = z.infer<typeof TaskClassSchema>;

/**
 * The task classes in SCHEDULING order, highest-priority FIRST — the index IS the
 * rank: `interactive` (0) → `coding` (1) → `ci` (2) → `gardener` (3). The claim
 * (`claimNextReady`) and the queue view (`queueView`) order by this rank BEFORE
 * `priority`/age, so an interactive session is never queued behind a deep coding
 * backlog and a background `gardener` box-maintenance task drains only once nothing
 * higher is waiting. The ONE source of truth for the order: the DB builds its ordering
 * `CASE` from this tuple ({@link taskClassRank}) and the idle-box arbiter
 * (`pickLaneForIdleBox`) reads the same classes, so the queue order and the lane
 * arbitration can never disagree.
 */
export const TASK_CLASS_SCHEDULE_ORDER = [
  TaskClass.Interactive,
  TaskClass.Coding,
  TaskClass.Ci,
  TaskClass.Gardener,
] as const;

/**
 * The scheduling rank of a {@link TaskClass} — its index in
 * {@link TASK_CLASS_SCHEDULE_ORDER} (lower = claimed first). An unrecognized value
 * ranks as `coding` (the {@link taskClassOf} default), so a legacy / malformed class
 * degrades to the neutral middle rather than jumping ahead of interactive work or
 * sinking below gardener maintenance.
 */
export function taskClassRank(taskClass: TaskClass): number {
  const rank = TASK_CLASS_SCHEDULE_ORDER.indexOf(taskClass);
  return rank === -1 ? TASK_CLASS_SCHEDULE_ORDER.indexOf(TaskClass.Coding) : rank;
}
